import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Collapse,
} from 'reactstrap';

// Shared read-only cargo table (Type / OT / Volume / DDR / DDR Notes / Incoterms
// / Incoterms Notes / Tujuan, expanding to the Crude/Condensate sub-table with
// both % Bleeding columns). TenderOpening is a sibling under PlanAndProcurement.
import CargoTable from '../../TenderOpening/components/CargoTable';
import { formatMonth, formatDateTime } from '../../formatters';

// "Monday, 05 Aug 2026" in Jakarta time — the DOD asks for "Hari, Tanggal".
const formatDayDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  });
};

// Invitation - Internal > Invitation tab — VIEW ONLY.
//
// Sections in the DOD's order:
//   a. Invitation Detail — from the "Invitation to Panitia Lelang" creation.
//   b. Contract, c. Cargo Specification, d. Clause Detail, e. Other Clause,
//   f. Validity — all carried over from Planning.
const InvitationTab = ({ detail }) => {
  const inv = detail?.invitationDetail || {};
  const req = detail?.requirement || {};
  const validity = req.validity || {};
  const [openClauses, setOpenClauses] = useState({});

  return (
    <div>
      <div className='mb-3'>
        <h5 className='mb-1'>
          <i className='ri-mail-open-line me-2'></i>
          Invitation Information
        </h5>
        <p className='text-muted mb-0 fs-12'>
          Tender-opening session details and the requirement issued during
          Planning (view only).
        </p>
      </div>

      {/* ---- a. Invitation Detail ---- */}
      <Card className='border shadow-sm mb-3'>
        <CardHeader className='fw-semibold bg-light'>
          Invitation Detail
        </CardHeader>
        <CardBody>
          <Row className='g-3'>
            <Col lg={6} sm={6}>
              <Label className='form-label'>Hari, Tanggal</Label>
              <Input value={formatDayDate(inv.date)} readOnly disabled />
            </Col>
            <Col lg={6} sm={6}>
              <Label className='form-label'>Waktu (Start - End)</Label>
              <Input
                value={
                  inv.timeStart && inv.timeEnd
                    ? `${inv.timeStart} - ${inv.timeEnd} WIB`
                    : '-'
                }
                readOnly
                disabled
              />
            </Col>
            <Col lg={6} sm={6}>
              <Label className='form-label'>Tempat</Label>
              <Input value={inv.place || '-'} readOnly disabled />
            </Col>
            <Col lg={6} sm={6}>
              <Label className='form-label'>Agenda</Label>
              <Input value={inv.agenda || '-'} readOnly disabled />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className='border shadow-sm'>
        <CardBody>
          {/* ---- b. Contract ---- */}
          <p className='form-label mb-1 fs-13 text-uppercase fw-semibold'>
            Contract
          </p>
          <Row className='g-3'>
            <Col lg={4} sm={6}>
              <Label className='form-label'>Contract Type</Label>
              <Input value={req.contractType || '-'} readOnly disabled />
            </Col>
            <Col lg={4} sm={6}>
              <Label className='form-label'>Start Period</Label>
              <Input
                value={formatMonth(req.startPeriod) || '-'}
                readOnly
                disabled
              />
            </Col>
            <Col lg={4} sm={6}>
              <Label className='form-label'>End Period</Label>
              <Input
                value={formatMonth(req.endPeriod) || '-'}
                readOnly
                disabled
              />
            </Col>
          </Row>

          {/* ---- c. Cargo Specification ---- */}
          <div className='mt-4 pt-3 border-top'>
            <p className='form-label mb-1 fs-13 text-uppercase fw-semibold'>
              Cargo Specification
            </p>
            <CargoTable cargos={req.cargos} variant='requirement' />
          </div>

          {/* ---- d. Clause Detail ---- */}
          <div className='mt-4 pt-3 border-top'>
            <p className='form-label mb-2 fs-13 text-uppercase fw-semibold'>
              Clause Detail
            </p>
            {(req.clauses || []).length === 0 ? (
              <p className='text-muted fs-12 mb-0'>No clause data</p>
            ) : (
              (req.clauses || []).map(c => {
                const open = !!openClauses[c.clauseId];
                return (
                  <Card className='border mb-2' key={c.clauseId}>
                    <CardHeader
                      role='button'
                      className='d-flex align-items-center justify-content-between py-2'
                      onClick={() =>
                        setOpenClauses(prev => ({
                          ...prev,
                          [c.clauseId]: !open,
                        }))
                      }
                    >
                      <span className='fw-semibold'>{c.title}</span>
                      <i
                        className={`ri-arrow-${open ? 'up' : 'down'}-s-line fs-20`}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={open}>
                      <CardBody>
                        <div className='form-check form-switch mb-3'>
                          <Input
                            type='checkbox'
                            role='switch'
                            className='form-check-input'
                            checked={!!c.mandatory}
                            disabled
                          />
                          <Label className='form-check-label'>
                            Mandatory clause for Registered Supplier to fulfill
                          </Label>
                        </div>
                        <Label className='form-label'>Summary Note</Label>
                        <Input
                          type='textarea'
                          rows={4}
                          value={c.summernote || ''}
                          disabled
                        />
                      </CardBody>
                    </Collapse>
                  </Card>
                );
              })
            )}
          </div>

          {/* ---- e. Other Clause ---- */}
          <div className='mt-4 pt-3 border-top'>
            <p className='form-label mb-1 fs-13 text-uppercase fw-semibold'>
              Other Clause
            </p>
            <Label className='form-label'>
              Other Term and Condition, Summary Note
            </Label>
            <Input
              type='textarea'
              rows={5}
              value={req.additionalNotes || ''}
              disabled
            />
          </div>

          {/* ---- f. Validity ---- */}
          <div className='mt-4 pt-3 border-top'>
            <p className='form-label mb-1 fs-13 text-uppercase fw-semibold'>
              Validity
            </p>
            <Row className='g-3'>
              <Col lg={6} sm={6}>
                <Label className='form-label'>
                  Submission Deadline (Jakarta Time)
                </Label>
                <Input
                  value={formatDateTime(validity.submissionDeadline)}
                  readOnly
                  disabled
                />
              </Col>
              <Col lg={6} sm={6}>
                <Label className='form-label'>
                  Validity Period (Jakarta Time)
                </Label>
                <Input
                  value={formatDateTime(validity.validityPeriod)}
                  readOnly
                  disabled
                />
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default InvitationTab;
